import React from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {AppBar, Toolbar, Typography, Button, Box, Menu, MenuItem, Divider, ListItemIcon} from '@mui/material';
import {useAuth} from "../authentication/AuthContext";
import {signOut} from 'firebase/auth';
import {auth} from "../firebase-config";
import UserAvatar from "./UserAvatar";
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import {Zap} from 'lucide-react';
import {useDailyStats} from '../reports/hooks/useDailyStats';

function Header() {
    const {user, authToken} = useAuth()
    const {data: dailyStats, isLoading: isScoreLoading} = useDailyStats(authToken);
    const productivityScore = dailyStats?.productivityScore ?? 0;
    const avatarUsername = user?.displayName?.trim() || user?.email?.split('@')[0]?.trim() || 'User';
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate()

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSubmit = () => {
        navigate('/settings');
        setAnchorEl(null);
    };

    const handleLogout = () => {
        signOut(auth).then(() => {
            navigate('/')
        }).catch((err) => console.log("logout failed", err))
    }

    const handleSignin = () => {
        navigate('/signin')
    }

    return (
        <Box>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 2,
                    background: 'var(--header-bg)',
                    backdropFilter: 'blur(10px)',
                    borderBottom: '1px solid var(--border-color)',
                    color: 'var(--text-primary)',
                }}
            >
                <Toolbar sx={{minHeight: '64px', px: {xs: 2, sm: 3}}}>
                    <Typography variant="h6" component="div" sx={{flexGrow: 1}}>
                        <Link to='/home' className="inline-flex items-center gap-2">
                            <img
                                src="/compitino_logo_horizontal_dark.svg"
                                alt="Compitino"
                                className="theme-logo-dark h-[42px] w-[168px] object-contain"
                            />
                            <img
                                src="/compitino_logo_horizontal.svg"
                                alt=""
                                aria-hidden="true"
                                className="theme-logo-light h-[42px] w-[168px] object-contain"
                            />
                        </Link>
                    </Typography>
                    <div className="flex items-center gap-2">
                        {user && (
                            <Link
                                to="/reports"
                                title="Today's productivity score"
                                aria-label={`Today's productivity score: ${isScoreLoading ? 'loading' : productivityScore}`}
                                className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-[var(--brand-coral)]/40 bg-[var(--brand-coral)]/10 px-3 text-sm font-semibold text-[var(--text-primary)] transition hover:border-[var(--brand-coral)] hover:bg-[var(--brand-coral)]/15"
                            >
                                <Zap size={15} aria-hidden="true"/>
                                <span>{isScoreLoading ? '—' : productivityScore}</span>
                            </Link>
                        )}
                        {user ? <Button
                            id="basic-button"
                            color='inherit'
                            sx={{borderRadius: '12px', minWidth: '42px', p: 0.5}}
                            aria-controls={open ? 'basic-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleClick}>
                            <UserAvatar username={avatarUsername}/>
                        </Button> : <Button
                            color="inherit"
                            onClick={handleSignin}
                            sx={{
                                borderColor: 'var(--border-strong)',
                                borderRadius: '10px',
                                textTransform: 'none',
                                '&:hover': {borderColor: 'var(--brand-coral)'},
                            }}
                            variant="outlined"
                        >
                            Sign In
                        </Button>}
                    </div>
                    <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1,
                                minWidth: 240,
                                borderRadius: '14px',
                                border: '1px solid var(--border-color)',
                                background: 'var(--menu-bg)',
                                color: 'var(--text-primary)',
                                boxShadow: 'var(--menu-shadow)',
                            }
                        }}
                        slotProps={{
                            list: {
                                'aria-labelledby': 'basic-button',
                                sx: { py: 1 }
                            },
                        }}
                    >
                        <Box sx={{ px: 2, py: 1 }}>
                            <Typography sx={{ fontSize: 12, color: 'var(--text-muted)' }}>Signed in as</Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                                {user?.displayName || user?.email || 'Compito User'}
                            </Typography>
                        </Box>

                        <Divider sx={{ borderColor: 'var(--border-color)' }} />

                        <MenuItem onClick={handleSubmit} sx={{ gap: 1.25, py: 1.1 }}>
                            <ListItemIcon sx={{ color: 'var(--brand-lavender)', minWidth: 30 }}>
                                <SettingsOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            Settings
                        </MenuItem>

                        <MenuItem onClick={handleLogout} sx={{ gap: 1.25, py: 1.1, color: 'var(--brand-coral)' }}>
                            <ListItemIcon sx={{ color: 'var(--brand-coral)', minWidth: 30 }}>
                                <LogoutRoundedIcon fontSize="small" />
                            </ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
        </Box>
    )
}

export default Header;
